/**
 * Admin route paths.
 *
 * Every path here mirrors `ROUTES.admin` from `@genex/contracts` **verbatim** — the
 * auth/flags/providers routes the API serves today, and the users/KYC/transactions/
 * reconciliation/brand/gift-card routes the admin-API track owns under the same shape.
 * They live here as a thin runtime constant so the console keeps no runtime dependency
 * on the contracts package (types still come from it, type-only). `API_PREFIX` matches
 * the contract's `/v1`. If a path is edited in the contract, this mirror must follow —
 * the shared route table is the single source of where anything lives.
 */
export const API_PREFIX = '/v1';

export const ADMIN_ROUTES = {
  // ── auth / flags / providers (live in the API today) ──
  login: '/admin/auth/login',
  me: '/admin/auth/me',
  // ── remember me, server side ──
  // `refresh` spends a rotating remember-me token for a fresh hour of access and a fresh
  // token; `logout` burns the whole lineage. Both are what make a 30-day console session
  // real rather than a client-side wish: the access token is still an hour, because a
  // thirty-day bearer token is one nobody can revoke.
  refresh: '/admin/auth/refresh',
  logout: '/admin/auth/logout',
  flags: '/admin/flags',
  flag: (key: string) => `/admin/flags/${encodeURIComponent(key)}`,
  providers: '/admin/providers',
  provider: (capability: string) => `/admin/providers/${encodeURIComponent(capability)}`,

  // ── provider credentials & activation ──
  // A different question from `providers` above, which is *routing*: given several
  // vendors that can all move naira, which order does failover try them in. These two
  // are *activation*: is a vendor configured at all, and is the operator willing to
  // send real money through it yet. Addressed by vendor slug, not by capability.
  providerCredentials: '/admin/provider-credentials',
  /** What address we call vendors from, and whether one of them accepts it. */
  providerEgress: '/admin/provider-egress',
  providerCredential: (slug: string) => `/admin/provider-credentials/${encodeURIComponent(slug)}`,

  // ── live rates + the operator override (GET the board, PUT/DELETE one pair, reset all) ──
  rates: '/admin/rates',
  ratesReset: '/admin/rates/reset',
  rate: (base: string, quote: string) =>
    `/admin/rates/${encodeURIComponent(base)}/${encodeURIComponent(quote)}`,

  // ── deposit rails: the minimum deposit and confirmation depth per (currency, network) ──
  // Two path segments rather than one joined key, exactly as the rate pair above. A
  // network's identity is its label — `Tron (TRC-20)`, spaces and brackets included — so
  // there is no separator to invent and no way to address half a rail.
  assetRails: '/admin/assets/rails',
  assetRail: (currency: string, network: string) =>
    `/admin/assets/rails/${encodeURIComponent(currency)}/${encodeURIComponent(network)}`,

  // ── fees: the house spread, every basis-point fee, and the bill schedule ──
  // Addressed by catalogue key (`spread_bps`, `conversion_fee_bps`), which is also what the
  // audit trail records — so the path an operator changed a fee through and the row that
  // says they did name the same thing.
  fees: '/admin/fees',
  fee: (key: string) => `/admin/fees/${encodeURIComponent(key)}`,

  // ── users ──
  users: '/admin/users',
  user: (id: string) => `/admin/users/${encodeURIComponent(id)}`,
  suspendUser: (id: string) => `/admin/users/${encodeURIComponent(id)}/suspend`,
  unsuspendUser: (id: string) => `/admin/users/${encodeURIComponent(id)}/unsuspend`,
  /**
   * The account state, written directly: `active`, `suspended`, `inactive` or `locked`.
   *
   * `suspendUser`/`unsuspendUser` above remain because a shipped console build calls them;
   * they are two named shortcuts onto this one path, so there is a single write and a single
   * audit shape rather than three that can drift apart.
   */
  userState: (id: string) => `/admin/users/${encodeURIComponent(id)}/state`,
  /**
   * A customer's profile photo, as raw bytes, for the console.
   *
   * Its own route rather than `/profile/photo`, which resolves the photo from the *caller's*
   * token — a console fetching that would render the operator's own face on every customer
   * row. Bearer-scoped and binary, so it goes through the transport's media path and never
   * through `JSON.parse`.
   */
  userPhoto: (id: string) => `/admin/users/${encodeURIComponent(id)}/photo`,
  /** Credit or debit a customer's balance with a narration. Double-entry, audited. */
  userBalance: (id: string) => `/admin/users/${encodeURIComponent(id)}/balance`,

  // ── KYC review — a single queue with a per-item review decision ──
  kycQueue: '/admin/kyc/queue',
  kycItem: (id: string) => `/admin/kyc/queue/${encodeURIComponent(id)}`,
  kycReview: (id: string) => `/admin/kyc/queue/${encodeURIComponent(id)}/review`,
  /** Granting a tier with no submission behind it. See the API's own note on the route. */
  userGrantTier: (id: string) => `/admin/users/${encodeURIComponent(id)}/grant-tier`,
  /** Metadata for the documents filed with one submission. Never the bytes. */
  kycDocuments: (id: string) => `/admin/kyc/queue/${encodeURIComponent(id)}/documents`,
  /**
   * One document's bytes, addressed by the *document* id rather than the submission's.
   *
   * Its own route rather than a field on the detail read, so that opening the queue is not
   * the same act as looking at somebody's passport, and so the audit row this write produces
   * names the one document that was actually viewed.
   */
  kycDocument: (id: string) => `/admin/kyc/documents/${encodeURIComponent(id)}`,

  // ── KYC failover, back towards the machine, and the queue's watchdog ──
  // `kycRecheck` re-runs the automated vendor chain against a submission already sitting
  // in the manual queue. The queue exists because the chain could not decide, and the
  // condition that put it there — a vendor down, a lookup that timed out — is usually
  // temporary, so a reviewer should be able to ask the machine again before a person is
  // spent on it. `kycPending` is the watchdog: how deep the queue is and how old its
  // oldest item is, on its own cheap path so a dashboard can poll it without paging the
  // queue itself.
  kycRecheck: (id: string) => `/admin/kyc/queue/${encodeURIComponent(id)}/recheck`,
  kycPending: '/admin/kyc/pending',

  // ── Per-tier transaction ceilings — the numbers every outflow is checked against ──
  // Compliance policy rather than white-label configuration, which is why they live under
  // `/admin/limits` and are gated on the KYC-review grant rather than on `admins:write`.
  limitTiers: '/admin/limits/tiers',
  limitTier: (tier: string) => `/admin/limits/tiers/${encodeURIComponent(tier)}`,

  // ── transactions + reconciliation ──
  transactions: '/admin/transactions',
  transaction: (id: string) => `/admin/transactions/${encodeURIComponent(id)}`,
  reconciliation: '/admin/reconciliation',
  runReconciliation: '/admin/reconciliation/run',
  /** What the custodian holds under individual customers, and the sweep back to the master. */
  custodySweep: '/admin/treasury/custody-sweep',
  /** The main balance going OUT to an external address. Leaves the platform; cannot be undone. */
  payout: '/admin/treasury/payout',

  // ── brand assets (GET the current logo metadata, PUT to replace it) ──
  brandLogo: '/admin/brand/logo',

  // ── the app splash screen (GET the current asset's metadata, PUT/POST to replace it) ──
  // A sibling of `brandLogo` rather than the same path because the two accept different
  // things: a logo is a small raster mark, a splash is a *portrait* image or a short
  // `video/mp4` under a ceiling six times larger. The bytes themselves are served publicly
  // at `/config/splash`, exactly as the logo's are at `/config/logo`; only metadata comes
  // back from here.
  brandSplash: '/admin/brand/splash',

  /**
   * The brand icon, and the balance-header artwork.
   *
   * The icon is a sibling of the logo rather than the same path: a logo is a wordmark and is
   * wide, an icon is a square mark, and one asset asked to be both is cropped badly on one
   * surface or letterboxed on the other. The hero is parameterised on which balance header it
   * sits behind, because the two are the same asset in every respect except that.
   */
  brandIcon: '/admin/brand/icon',
  brandHero: (surface: string) => `/admin/brand/hero/${encodeURIComponent(surface)}`,
  /** The text-colour choice over one header. Its own route; see the API's note. */
  brandHeroText: (surface: string) => `/admin/brand/hero/${encodeURIComponent(surface)}/text`,

  // ── bank directory (GET what the worker synced, PUT one bank's logo) ──
  // Addressed by the bank's opaque public id, never its NIBSS code.
  banks: '/admin/banks',
  bankLogo: (bankId: string) => `/admin/banks/${encodeURIComponent(bankId)}/logo`,

  // ── home-screen banners (list/create, patch/delete one, PUT its artwork) ──
  banners: '/admin/banners',
  banner: (id: string) => `/admin/banners/${encodeURIComponent(id)}`,
  bannerImage: (id: string) => `/admin/banners/${encodeURIComponent(id)}/image`,

  // ── gift-card redemption review + brand catalogue ──
  giftcardQueue: '/admin/giftcards/queue',
  giftcardItem: (id: string) => `/admin/giftcards/queue/${encodeURIComponent(id)}`,
  giftcardReview: (id: string) => `/admin/giftcards/queue/${encodeURIComponent(id)}/review`,
  /**
   * The brand catalogue: GET to list (including inactive brands, which the customer
   * catalogue never shows), POST to create, and `giftcardBrand(id)` to PATCH one.
   *
   * A different grant from the review queue above, and deliberately so: deciding a
   * redemption is a compliance act, while setting a brand's rate is the multiplier every
   * future payout for that brand runs through — a treasury act, gated on the roles holding
   * `rates:write`. The *list* is gated the same way as the writes, so a role without that
   * grant is refused on the read too, not merely on the buttons.
   */
  giftcardBrands: '/admin/giftcards/brands',
  giftcardBrand: (id: string) => `/admin/giftcards/brands/${encodeURIComponent(id)}`,
  giftcardBrandLogo: (id: string) => `/admin/giftcards/brands/${encodeURIComponent(id)}/logo`,

  // ── bill services, operator side ──
  // The catalogue was seed-only until now: `billServices` lists every service including the
  // ones the app hides, and `billServiceIcon` replaces one service's artwork. The bytes are
  // served back publicly at `publicBillServiceIcon`, because a tile in a list renders before
  // any token is attached and an authenticated image there is a broken image.
  billServices: '/admin/bills/services',
  billServiceIcon: (id: string) => `/admin/bills/services/${encodeURIComponent(id)}/icon`,
  publicBillServiceIcon: (id: string) => `/bills/services/${encodeURIComponent(id)}/icon`,

  // ── support (operator inbox, thread detail, reply/assign, settings, media) ──
  supportInbox: '/admin/support/inbox',
  supportThread: (id: string) => `/admin/support/threads/${encodeURIComponent(id)}`,
  supportReply: (id: string) => `/admin/support/threads/${encodeURIComponent(id)}/reply`,
  supportAssign: (id: string) => `/admin/support/threads/${encodeURIComponent(id)}/assign`,
  supportSettings: '/admin/support/settings',
  supportMedia: (id: string) => `/admin/support/media/${encodeURIComponent(id)}`,

  // ── Business Settings — the white-label config hub (GET/PUT the singleton row) ──
  settingsBusiness: '/admin/settings/business',

  // ── Legal & about documents (GET all three as stored, PUT to publish one) ──
  legalDocuments: '/admin/legal',
  legalDocument: (key: string) => `/admin/legal/${encodeURIComponent(key)}`,

  // ── Editable email templates (list, upsert one, render a preview, send a live test) ──
  // `restore` puts one back to the shipped default, snapshotting the current content into the
  // history first, so it is recoverable rather than destructive.
  emailTemplates: '/admin/email-templates',
  emailTemplate: (key: string) => `/admin/email-templates/${encodeURIComponent(key)}`,
  emailTemplatePreview: (key: string) => `/admin/email-templates/${encodeURIComponent(key)}/preview`,
  emailTemplateTestSend: (key: string) => `/admin/email-templates/${encodeURIComponent(key)}/test-send`,
  emailTemplateRestore: (key: string) => `/admin/email-templates/${encodeURIComponent(key)}/restore`,
  emailTemplateVersions: (key: string) => `/admin/email-templates/${encodeURIComponent(key)}/versions`,

  // ── The email layout — the branded chrome every message is poured into ──
  // Append-only: `emailLayout` PUT inserts a new version and moves the active marker rather
  // than overwriting, so a palette an operator regrets is one restore away rather than gone.
  emailLayout: '/admin/email-layout',
  emailLayoutVersions: '/admin/email-layout/versions',
  emailLayoutRestore: '/admin/email-layout/restore',
  emailLayoutPreview: '/admin/email-layout/preview',

  // ── Images an admin may place in mail ──
  // The *serving* route is public and lives outside the admin block, because the client
  // fetching it is the recipient's mail app and it has no session to present.
  emailImages: '/admin/email-images',
  emailImage: (id: string) => `/admin/email-images/${encodeURIComponent(id)}`,
  /** The public byte-serve path a stored image URL resolves to. Mirrors `ROUTES.emailImage`. */
  publicEmailImage: (id: string) => `/email/images/${encodeURIComponent(id)}`,

  // ── Broadcasts — branded mail and newsletters ──
  // Two steps on purpose: `emailBroadcasts` POST creates a draft and resolves nothing,
  // `emailBroadcastSend` is the irreversible act.
  emailBroadcasts: '/admin/email-broadcasts',
  emailBroadcastsPreview: '/admin/email-broadcasts/preview',
  emailBroadcast: (id: string) => `/admin/email-broadcasts/${encodeURIComponent(id)}`,
  emailBroadcastSend: (id: string) => `/admin/email-broadcasts/${encodeURIComponent(id)}/send`,
  emailBroadcastCancel: (id: string) => `/admin/email-broadcasts/${encodeURIComponent(id)}/cancel`,

  // ── Prove the SMTP settings before they are trusted with real mail ──
  // Nothing is saved by this route: the point is that a configuration which cannot deliver
  // never becomes the live one. The settings themselves are provider credentials.
  smtpTest: '/admin/smtp/test',

  // ── Push, operator side — a superadmin broadcast fans a message out to the base ──
  pushBroadcast: '/admin/push/broadcast',

  // ── The treasury desk ──
  // `overview` is live balances against the ledger; `orders`/`order(id)` the full drill-down
  // including every leg and raw vendor payload; `review` the manual queue and `reviewOrder`
  // the decision; `rebalances` records an OTC move the desk already made; `thresholds` the
  // liquidity floors and the pause switch; `reconciliation` the runs and their open deltas.
  //
  // Reads are any admin. Every write is finance — a review decision changes what a customer
  // is paid, and the thresholds move the limits the automated gate refuses on.
  treasuryOverview: '/admin/treasury/overview',
  treasuryOrders: '/admin/treasury/orders',
  treasuryOrder: (id: string) => `/admin/treasury/orders/${encodeURIComponent(id)}`,
  treasuryReview: '/admin/treasury/review',
  treasuryReviewOrder: (id: string) => `/admin/treasury/review/${encodeURIComponent(id)}`,
  treasuryRebalances: '/admin/treasury/rebalances',
  treasuryThresholds: '/admin/treasury/thresholds',
  treasuryReconciliation: '/admin/treasury/reconciliation',
  treasuryReconciliationRun: '/admin/treasury/reconciliation/run',
} as const;
